/**
 * Entrer dans une partie, et attendre les autres.
 *
 * Deux écrans qui n'en font qu'un : on crée une partie ou on en rejoint une par son
 * code, puis on patiente ensemble. Le code **est** l'identifiant de la Room — pas un
 * secret, juste ce qu'on se dit à travers la table ou par message.
 */

import { PROFILS } from "@zinc421/shared";

import { useTextes } from "../i18n/contexte.js";
import type { EnLigne } from "./connexion.js";

/** L'entrée : créer, ou rejoindre par code. */
export function EntreeEnLigne({
  enLigne, nom, code, onNom, onCode, onQuitter,
}: {
  readonly enLigne: EnLigne;
  readonly nom: string;
  readonly code: string;
  readonly onNom: (v: string) => void;
  readonly onCode: (v: string) => void;
  readonly onQuitter: () => void;
}) {
  const textes = useTextes();
  const occupe = enLigne.statut === "connexion";
  return (
    <div className="accueil">
      <h1>{textes.enLigneTitre}</h1>
      <p className="accroche">{textes.enLigneAccroche}</p>

      <fieldset className="reglage">
        <legend>{textes.tonNomATable}</legend>
        <input
          className="nom"
          type="text"
          value={nom}
          maxLength={16}
          aria-label={textes.tonNom}
          placeholder={textes.tonNom}
          onChange={(e) => onNom(e.target.value)}
        />
      </fieldset>

      <button
        className="bouton bouton--large"
        disabled={occupe}
        onClick={() => enLigne.creer(nom)}
      >
        {occupe ? textes.onOuvreLaTable : textes.ouvrirUnePartie}
      </button>

      <fieldset className="reglage">
        <legend>{textes.ouRejoindreAvecUnCode}</legend>
        <div className="rejoindre">
          <input
            className="nom code-saisi"
            type="text"
            value={code}
            maxLength={12}
            aria-label={textes.codeDeLaPartie}
            placeholder={textes.codeRecu}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => onCode(e.target.value)}
          />
          <button
            className="bouton"
            disabled={occupe || code.trim().length === 0}
            onClick={() => enLigne.rejoindre(code, nom)}
          >
            Rejoindre
          </button>
        </div>
      </fieldset>

      {enLigne.erreur && <p className="alerte" role="alert">{enLigne.erreur}</p>}

      <button className="bouton bouton--discret" onClick={onQuitter}>
        {textes.revenirAuComptoir}
      </button>
    </div>
  );
}

/** La salle d'attente : le code à partager, qui est là, et qui manque. */
export function Salon({ enLigne }: { readonly enLigne: EnLigne }) {
  const textes = useTextes();
  const manque = 2 - enLigne.joueurs.length;
  return (
    <div className="accueil">
      <h1>{textes.salleDAttente}</h1>
      <p className="accroche">{textes.partageCeCode}</p>

      <p className="code-partie" aria-label={textes.codeDeLaPartie}>{enLigne.code}</p>

      <fieldset className="reglage">
        <legend>
          {enLigne.joueurs.length} joueur{enLigne.joueurs.length > 1 ? "s" : ""} à table
        </legend>
        <ul className="zinc zinc--salon" aria-label={textes.lesJoueursPresents}>
          {enLigne.joueurs.map((j) => (
            <li key={j.siege} className={`joueur${j.siege === enLigne.monSiege ? " joueur--main" : ""}`}>
              <span className="joueur-nom">{j.nom}</span>
              <span className="joueur-jetons">
                {j.estIA ? textes.joueurVirtuel : j.siege === enLigne.monSiege ? "toi" : textes.joueurEnLigne}
              </span>
            </li>
          ))}
        </ul>
      </fieldset>

      {enLigne.estHote ? (
        <>
          <fieldset className="reglage">
            <legend>{textes.completerAvecUnVirtuel}</legend>
            <ul className="niveaux">
              {PROFILS.map((profil) => (
                <li key={profil.niveau}>
                  <button
                    type="button"
                    className="niveau"
                    onClick={() => enLigne.ajouterIA(
                      profil.niveau, textes.adversaireNom(profil.cle))}
                  >
                    <span className="niveau-nom">{textes.adversaireNom(profil.cle)}</span>
                    <span className="niveau-taux">{textes.niveauNumero(profil.niveau)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </fieldset>
          <button
            className="bouton bouton--large"
            disabled={enLigne.joueurs.length < 2}
            onClick={enLigne.demarrer}
          >
            {enLigne.joueurs.length < 2
              ? textes.encoreDesJoueurs(manque)
              : textes.commencerLaCave}
          </button>
        </>
      ) : (
        <p className="etape">{textes.aLHoteDeLancer}</p>
      )}

      <button className="bouton bouton--discret" onClick={enLigne.quitter}>
        {textes.quitterLaTable}
      </button>
    </div>
  );
}
